import type { SalesHarvestOrg } from "@xop/dashboard-shared";

import { centroidFor, displayCountry, type LatLng } from "./centroids.js";

/** One country's worth of harvested orgs, ready to plot as a single dot. */
export interface CountryAgg {
  /** Display name (full country name where known). */
  country: string;
  centroid: LatLng;
  orgs: SalesHarvestOrg[];
  orgCount: number;
  contactCount: number;
}

export interface AggregateResult {
  /** Countries with a known centroid, sorted by contact count desc. */
  countries: CountryAgg[];
  /** Orgs we couldn't place (no centroid for their country). */
  unmappedOrgCount: number;
  /** Distinct country labels we couldn't place. */
  unmappedCountries: string[];
  totalOrgs: number;
  totalContacts: number;
}

/**
 * Group harvested orgs by country and resolve each group's map centroid. Pure +
 * deterministic so it's unit-testable without a DOM/leaflet. Orgs whose country
 * has no centroid are counted as `unmapped` rather than dropped silently.
 */
export function aggregateByCountry(orgs: SalesHarvestOrg[]): AggregateResult {
  const groups = new Map<string, CountryAgg>();
  let unmappedOrgCount = 0;
  const unmapped = new Set<string>();
  let totalContacts = 0;

  for (const o of orgs) {
    const contacts = o.contact_count || 0;
    totalContacts += contacts;
    const centroid = centroidFor(o.region, o.country);
    if (!centroid) {
      unmappedOrgCount += 1;
      const label = (o.region || o.country || "").trim();
      if (label) unmapped.add(label);
      continue;
    }
    const country = displayCountry(o.region, o.country);
    const existing = groups.get(country);
    if (existing) {
      existing.orgs.push(o);
      existing.orgCount += 1;
      existing.contactCount += contacts;
    } else {
      groups.set(country, {
        country,
        centroid,
        orgs: [o],
        orgCount: 1,
        contactCount: contacts,
      });
    }
  }

  const countries = [...groups.values()].sort(
    (a, b) => b.contactCount - a.contactCount || b.orgCount - a.orgCount,
  );

  return {
    countries,
    unmappedOrgCount,
    unmappedCountries: [...unmapped].sort(),
    totalOrgs: orgs.length,
    totalContacts,
  };
}

/** Marker radius (px) for a country, scaled by contact count with a floor +
 * ceiling so a 1-contact dot is still tappable and a huge one doesn't swamp
 * the map. Exported for testing the scaling curve. */
export function markerRadius(contactCount: number): number {
  return Math.round(7 + Math.min(26, Math.sqrt(Math.max(0, contactCount)) * 3.5));
}
